import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import localStore from '@shared/utils/localStore.ts';

import type { TrainerMeDto, TrainerTokensDto } from '../services/trainerAuthApi.ts';

interface TrainerAuthState {
  tokens?: { accessToken: string | null; refreshToken: string | null };
  isAuthenticated: boolean;
  trainerAccountId?: string;
  trainer?: TrainerMeDto;
}

interface RootStateWithTrainerAuth {
  trainerAuth: TrainerAuthState;
  [key: string]: unknown;
}

const initialState: TrainerAuthState = {
  tokens: localStore.getTrainerTokens(),
  isAuthenticated: localStore.getTrainerTokens().accessToken !== null,
  trainerAccountId: localStore.getTrainerAccountId(),
  trainer: undefined,
};

export const trainerAuthSlice = createSlice({
  name: 'trainerAuth',
  initialState,
  reducers: {
    trainerLogin: (state, action: PayloadAction<TrainerTokensDto>) => {
      const { accessToken, refreshToken, trainerAccountId } = action.payload;
      localStore.setTrainerTokens({ accessToken, refreshToken });
      localStore.setTrainerAccountId(trainerAccountId);
      state.tokens = { accessToken, refreshToken };
      state.trainerAccountId = trainerAccountId;
      state.isAuthenticated = true;
    },
    trainerLogout: state => {
      state.tokens = undefined;
      state.isAuthenticated = false;
      state.trainerAccountId = undefined;
      state.trainer = undefined;
      localStore.clearTrainerTokens();
      localStore.deleteTrainerAccountId();
    },
    trainerUpdateTokens: (state, action: PayloadAction<TrainerTokensDto>) => {
      const { accessToken, refreshToken, trainerAccountId } = action.payload;
      localStore.setTrainerTokens({ accessToken, refreshToken });
      localStore.setTrainerAccountId(trainerAccountId);
      state.tokens = { accessToken, refreshToken };
      state.trainerAccountId = trainerAccountId;
    },
    setTrainerProfile: (state, action: PayloadAction<TrainerMeDto>) => {
      state.trainer = action.payload;
      state.trainerAccountId = action.payload.id;
    },
  },
});

export const { trainerLogin, trainerLogout, trainerUpdateTokens, setTrainerProfile } =
  trainerAuthSlice.actions;

export const selectIsTrainerAuthenticated = (state: RootStateWithTrainerAuth) =>
  state.trainerAuth.isAuthenticated;

export const selectTrainerProfile = (state: RootStateWithTrainerAuth) => state.trainerAuth.trainer;

export default trainerAuthSlice.reducer;
